import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Download,
  X,
  RefreshCw,
  FileText,
  Clock,
  CheckCircle,
  AlertCircle,
  DownloadCloud
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import {
  BackgroundExportStatus,
  downloadExportFile,
  cancelBackgroundExport,
  DownloadProgress
} from '@/utils/organization/backgroundExport';
import {
  getDisplayErrorMessage,
  getDisplayExportStatus,
  getDisplayStepMessage,
  isExportActive,
  isExportReady,
} from '@/utils/organization/exportStatusDisplay';
import { useToast } from '@/hooks/use-toast';
import { getReportsExportDownloadFilename, getReportsExportItemLabel, readExportFileParts } from '@/shared/reportsTabLabel';

interface ReportsExportListProps {
  isOpen: boolean;
  onClose: () => void;
  organisationId: string;
  /** The merchant's name when the caller has it; saves the lookup and keeps the names equal to the New view's. */
  organisationName?: string;
}

export const ReportsExportList: React.FC<ReportsExportListProps> = ({
  isOpen,
  onClose,
  organisationId,
  organisationName,
}) => {
  const [exports, setExports] = useState<BackgroundExportStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchedOrgName, setFetchedOrgName] = useState<string>('');
  const [orgLookupDone, setOrgLookupDone] = useState(false);
  const orgName = organisationName || fetchedOrgName;
  // A name is only offered once the merchant name is known (given, or looked up), so a click
  // before the lookup finishes cannot download a file named for something else.
  const orgNameReady = Boolean(organisationName) || orgLookupDone;
  const { toast } = useToast();
  const channelRef = useRef<any>(null);

  // Fetch org name for download filename
  useEffect(() => {
    if (!organisationId || organisationName) return;
    supabase
      .from('organisations')
      .select('name')
      .eq('id', organisationId)
      .single()
      .then(
        ({ data }) => {
          if (data?.name) setFetchedOrgName(data.name);
        },
        () => undefined,
      )
      .then(() => setOrgLookupDone(true));
  }, [organisationId, organisationName]);

  // The full name of an export's file: default name plus the stored optional label (FIG-333).
  const getExportFileName = (exportItem: BackgroundExportStatus) => {
    const { label, asOfDate } = readExportFileParts(exportItem.custom_sql);
    return getReportsExportDownloadFilename(
      orgName,
      exportItem.table_name,
      exportItem.created_at,
      asOfDate,
      label,
    );
  };

  const fetchReportExports = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('csv_exports')
        .select('*')
        .eq('organisation_id', organisationId)
        .like('connection_id', 'report-%')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setExports((data as any) || []);
    } catch (error) {
      console.error('Failed to fetch report exports:', error);
      toast({
        title: "Error",
        description: "Failed to load report exports",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [organisationId, toast]);

  // Real-time subscription for report exports
  useEffect(() => {
    if (!isOpen) return;

    // Initial fetch
    fetchReportExports();

    // Setup realtime connection for all report exports
    const channel = supabase
      .channel('report-exports')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'csv_exports',
          filter: `organisation_id=eq.${organisationId}`
        },
        (payload) => {
          if (payload.new) {
            const updatedExport = payload.new as BackgroundExportStatus;

            setExports(currentExports => {
              const existingIndex = currentExports.findIndex(
                exp => exp.export_id === updatedExport.export_id
              );

              if (existingIndex >= 0) {
                // Update existing export
                const newExports = [...currentExports];
                newExports[existingIndex] = updatedExport;
                return newExports;
              } else {
                // Add new export
                return [updatedExport, ...currentExports];
              }
            });
          } else if (payload.old && payload.eventType === 'DELETE') {
            const deletedExport = payload.old as BackgroundExportStatus;
            setExports(currentExports =>
              currentExports.filter(exp => exp.export_id !== deletedExport.export_id)
            );
          }
        }
      )
      .subscribe((status) => {
        if (status === 'CHANNEL_ERROR') {
          console.warn('Realtime connection failed, will use polling fallback');
        }
      });

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [isOpen, organisationId, fetchReportExports]);

  const [downloadingExports, setDownloadingExports] = useState<Set<string>>(new Set());
  const [downloadProgress, setDownloadProgress] = useState<Record<string, DownloadProgress>>({});

  const handleDownload = async (exportItem: BackgroundExportStatus) => {
    if (!exportItem.download_url) {
      toast({
        title: "Download not available",
        description: "This export doesn't have a download URL yet",
        variant: "destructive"
      });
      return;
    }

    setDownloadingExports(prev => new Set(prev).add(exportItem.export_id));

    try {
      const filename = getExportFileName(exportItem);
      await downloadExportFile(
        exportItem.download_url,
        filename,
        (progress) => {
          setDownloadProgress(prev => ({
            ...prev,
            [exportItem.export_id]: progress
          }));
        },
        exportItem.export_id
      );
    } catch (error: any) {
      console.error('Download failed:', error);
      toast({
        title: "Download Failed",
        description: error.message || "Failed to download the export file",
        variant: "destructive"
      });
    } finally {
      setDownloadingExports(prev => {
        const newSet = new Set(prev);
        newSet.delete(exportItem.export_id);
        return newSet;
      });
      setDownloadProgress(prev => {
        const newProgress = { ...prev };
        delete newProgress[exportItem.export_id];
        return newProgress;
      });
    }
  };

  const handleCancel = async (exportId: string) => {
    try {
      const result = await cancelBackgroundExport(exportId);
      if (result.success) {
        toast({
          title: "Export Cancelled",
          description: "The export has been cancelled",
        });
      }
    } catch (error) {
      console.error('Failed to cancel export:', error);
      toast({
        title: "Error",
        description: "Failed to cancel export",
        variant: "destructive"
      });
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'finalizing':
      case 'processing':
      case 'queued':
      case 'combining':
        return <Clock className="h-4 w-4 text-blue-500 animate-spin" />;
      case 'failed':
        return <AlertCircle className="h-4 w-4 text-destructive" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'completed':
        return 'default';
      case 'finalizing':
      case 'processing':
      case 'queued':
      case 'combining':
        return 'secondary';
      case 'failed':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  if (!isOpen) return null;

  return (
    <Card className="w-full">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <CardTitle className="text-lg font-semibold flex items-center gap-2">
          <DownloadCloud className="h-5 w-5" />
          Report Exports
        </CardTitle>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={fetchReportExports}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[400px] pr-4">
          {loading && exports.length === 0 ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              Loading exports...
            </div>
          ) : exports.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
              <FileText className="h-8 w-8 mb-2" />
              <p>No report exports yet</p>
              <p className="text-sm">Export a report to see it here</p>
            </div>
          ) : (
            <div className="space-y-4">
              {exports.map((exportItem) => {
                const isDownloading = downloadingExports.has(exportItem.export_id);
                const progress = downloadProgress[exportItem.export_id];
                const displayStatus = getDisplayExportStatus(exportItem);
                const isReady = isExportReady(exportItem);
                const isActive = isExportActive(exportItem);
                const displayStep = getDisplayStepMessage(exportItem.current_step);
                const displayError = getDisplayErrorMessage(exportItem.error_message);

                return (
                  <div key={exportItem.export_id} className="border rounded-lg p-4 space-y-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3 flex-1">
                        {getStatusIcon(displayStatus)}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-medium truncate">
                              {getReportsExportItemLabel(exportItem.table_name)}
                            </h3>
                            <Badge variant={getStatusBadgeVariant(displayStatus)}>
                              {displayStatus}
                            </Badge>
                          </div>
                          {orgNameReady && (
                            <p className="text-xs break-all mt-1" data-testid="export-list-file-name">
                              {getExportFileName(exportItem)}
                            </p>
                          )}
                          <p className="text-sm text-muted-foreground mt-1">
                            {new Date(exportItem?.created_at).toLocaleString()}
                          </p>
                          {exportItem.actual_row_count !== null && (
                            <p className="text-sm text-muted-foreground">
                              {exportItem?.actual_row_count?.toLocaleString()} rows
                            </p>
                          )}
                          {displayStep && isActive && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {displayStep}
                            </p>
                          )}
                          {displayError && exportItem.status === 'failed' && (
                            <p className="text-xs text-destructive mt-1">
                              {displayError}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2 ml-2">
                        {isReady && (
                          <Button
                            size="sm"
                            onClick={() => handleDownload(exportItem)}
                            disabled={isDownloading || !orgNameReady}
                          >
                            {isDownloading ? (
                              <>
                                <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                                {progress?.percentage ? `${Math.round(progress.percentage)}%` : 'Downloading...'}
                              </>
                            ) : (
                              <>
                                <Download className="h-3 w-3 mr-1" />
                                Download
                              </>
                            )}
                          </Button>
                        )}
                        {isActive && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleCancel(exportItem.export_id)}
                          >
                            <X className="h-3 w-3 mr-1" />
                            Cancel
                          </Button>
                        )}
                      </div>
                    </div>
                    {isDownloading && progress && (
                      <div className="space-y-1">
                        <div className="flex justify-between text-xs text-muted-foreground">
                          <span>Downloading...</span>
                          <span>{Math.round(progress.percentage)}%</span>
                        </div>
                        <div className="w-full bg-secondary rounded-full h-2">
                          <div
                            className="bg-primary h-2 rounded-full transition-all duration-300"
                            style={{ width: `${progress.percentage}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </CardContent>
    </Card>
  );
};